import * as THREE from 'heritage/assets/three.module.js';

const V=(x,y,z=0)=>new THREE.Vector3(x,y,z);
const mix=(a,b,t)=>a+(b-a)*t;
const curve=points=>new THREE.CatmullRomCurve3(points,false,'centripetal');

// A single-stone illustration shared by the drawing, CAD, casting and setting.
export const ringReference={kind:'illustrative-solitaire',stoneCount:1};

function sweep(path,radiusStart,radiusEnd,depthRatio=1,segments=48){
  const geometry=new THREE.TubeGeometry(path,segments,1,12,false);
  const frames=path.computeFrenetFrames(segments,false),positions=geometry.attributes.position;
  for(let i=0;i<=segments;i++){
    const t=i/segments,center=path.getPointAt(t),radius=mix(radiusStart,radiusEnd,t);
    for(let j=0;j<=12;j++){
      const angle=j/12*Math.PI*2;
      const point=center.clone().addScaledVector(frames.normals[i],-Math.cos(angle)*radius).addScaledVector(frames.binormals[i],Math.sin(angle)*radius*depthRatio);
      positions.setXYZ(i*13+j,point.x,point.y,point.z);
    }
  }
  geometry.computeVertexNormals();return geometry;
}

function shankGeometry(){
  const geometry=new THREE.TorusGeometry(1.115,1,20,160);
  const positions=geometry.attributes.position;
  for(let j=0;j<=20;j++)for(let i=0;i<=160;i++){
    const angle=i/160*Math.PI*2,cross=j/20*Math.PI*2;
    const shoulder=Math.max(0,Math.sin(angle));
    const radial=.075+.008*shoulder,width=.115+.025*shoulder;
    const r=1.115+Math.cos(cross)*radial;
    positions.setXYZ(j*161+i,r*Math.cos(angle),r*Math.sin(angle),Math.sin(cross)*width);
  }
  geometry.computeVertexNormals();return geometry;
}

function gemGeometry(outline,girdle,table){
  const vertices=[],groups=[];
  const face=(a,b,c,material)=>{
    groups.push({start:vertices.length/3,count:3,materialIndex:material});
    vertices.push(...a.toArray(),...b.toArray(),...c.toArray());
  };
  const n=outline.length;
  const point=(index,scale,y)=>{const p=outline[(index%n+n)%n];return V(p.x*scale,y,p.y*scale);};
  // An octagonal table, star facets and kite-shaped bezels break up reflections.
  for(let i=0;i<8;i++){
    const k=i*4,t=point(k,.55,table),tn=point(k+4,.55,table);
    const star=point(k+2,.80,mix(table,girdle,.30));
    const previousStar=point(k-2,.80,mix(table,girdle,.30));
    const g=point(k,1,girdle);
    face(V(0,table,0),tn,t,0);
    face(t,tn,star,1+i%3);
    face(t,star,g,1+(i+1)%3);face(t,g,previousStar,1+(i+1)%3);
    for(let j=0;j<4;j++)face(star,point(k+j+1,1,girdle),point(k+j,1,girdle),1+(i+j)%3);
  }
  for(let i=0;i<16;i++){
    const k=i*2,mid=point(k+1,.57,girdle*.43),next=point(k+3,.57,girdle*.43);
    face(V(0,0,0),mid,next,1+i%3);
    face(mid,point(k,1,girdle),point(k+1,1,girdle),1+(i+1)%3);
    face(mid,point(k+1,1,girdle),point(k+2,1,girdle),1+(i+2)%3);
    face(mid,point(k+2,1,girdle),next,1+i%3);
  }
  const geometry=new THREE.BufferGeometry();
  geometry.setAttribute('position',new THREE.Float32BufferAttribute(vertices,3));
  groups.forEach(g=>geometry.addGroup(g.start,g.count,g.materialIndex));
  geometry.computeVertexNormals();return geometry;
}

export function buildRingStudy({object,part,pencil,wireMaterial,gemMaterials}){
  const round=Array.from({length:32},(_,i)=>new THREE.Vector2(Math.cos(i/32*Math.PI*2)*.46,Math.sin(i/32*Math.PI*2)*.46));
  const settings=[
    {name:'center',shape:'round',position:V(0,1.20),side:1,tilt:0,outline:round,girdle:.405,table:.56}
  ];
  const headPoint=(s,p)=>V(p.x*s.side,p.y,p.z*s.side).applyAxisAngle(V(0,0,1),s.tilt).add(s.position);
  const prongs=[];

  const shank=part(shankGeometry());shank.name='rounded-shank';
  const arc=r=>Array.from({length:161},(_,i)=>V(Math.cos(i/160*Math.PI*2)*r,Math.sin(i/160*Math.PI*2)*r));
  pencil(arc(1.193),0,0,.075);pencil(arc(1.035),0,.025,.10);
  pencil(arc(1.193),1,.025,.10,.006);pencil(arc(1.035),1,.045,.12,-.006);

  for(const [index,s] of settings.entries()){
    const head=new THREE.Group();head.name=`${s.name}-setting`;head.userData.shape=s.shape;
    head.position.copy(s.position);head.rotation.set(0,s.side<0?Math.PI:0,s.tilt,'ZYX');object.add(head);s.head=head;
    const geometry=gemGeometry(s.outline,s.girdle,s.table);
    const gem=new THREE.Mesh(geometry,gemMaterials);gem.name=`${s.name}-stone`;head.add(gem);s.gem=gem;
    const gemWire=new THREE.LineSegments(new THREE.EdgesGeometry(geometry,1),wireMaterial);gemWire.name=`${s.name}-stone-cad`;head.add(gemWire);s.gemWire=gemWire;

    // The gallery sits directly beneath each girdle rather than far below it.
    const railY=s.girdle-.055;
    const railPoints=s.outline.map(p=>V(p.x*.97,railY,p.y*.97));railPoints.push(railPoints[0].clone());
    const rail=part(sweep(curve(railPoints),.030,.030,1,96),V(0,0),null,head);rail.name=`${s.name}-basket`;

    const clawPoints=s.shape==='round'
      ?[Math.PI/4,Math.PI*3/4,Math.PI*5/4,Math.PI*7/4].map(a=>new THREE.Vector2(Math.cos(a)*.46,Math.sin(a)*.46))
      :[new THREE.Vector2(.32,0),new THREE.Vector2(-.19,.145),new THREE.Vector2(-.19,-.145)];
    for(const [i,point] of clawPoints.entries()){
      const endpoint=open=>V(point.x*(open?1.12:.95),s.girdle+(s.shape==='round'?.04:.024),point.y*(open?1.12:.95));
      const points=open=>[
        V(point.x*.17,-.045,point.y*.28),
        V(point.x*.50,s.girdle*.24,point.y*.55),
        V(point.x*.96,s.girdle*.69,point.y*.96),
        endpoint(open)
      ];
      const openPath=curve(points(true)),closedPath=curve(points(false));
      const openGeometry=sweep(openPath,s.shape==='round'?.047:.035,s.shape==='round'?.032:.025);
      const closedGeometry=sweep(closedPath,s.shape==='round'?.047:.035,s.shape==='round'?.032:.025);
      openGeometry.morphAttributes.position=[closedGeometry.attributes.position];
      openGeometry.morphAttributes.normal=[closedGeometry.attributes.normal];
      const stem=part(openGeometry,V(0,0),null,head);stem.name=`${s.name}-prong-${i+1}`;
      const tip=part(new THREE.SphereGeometry(s.shape==='round'?.035:.028,16,12),endpoint(true),null,head);
      tip.name=`${s.name}-prong-tip-${i+1}`;
      prongs.push({stem,tip,open:endpoint(true),closed:endpoint(false)});
      if(point.y>=0){
        pencil(openPath.getPoints(26).map(p=>headPoint(s,p)),0,.085+index*.008,.15+index*.009);
        pencil(openPath.getPoints(26).map(p=>headPoint(s,p).add(V(.02,0))),1,.10+index*.008,.16+index*.009);
      }
    }

    const minX=Math.min(...s.outline.map(p=>p.x)),maxX=Math.max(...s.outline.map(p=>p.x));
    const profile=[V(minX,s.girdle),V(minX*.55,s.table),V(maxX*.55,s.table),V(maxX,s.girdle),V(0,0),V(minX,s.girdle)];
    const line=pencil(profile.map(p=>headPoint(s,p)),0,.065+index*.012,.125+index*.012);line.name=`${s.name}-stone-profile`;
    pencil([V(minX,s.girdle),V(maxX,s.girdle)].map(p=>headPoint(s,p)),1,.09,.15);
    pencil([V(minX*.55,s.table),V(0,0),V(maxX*.55,s.table)].map(p=>headPoint(s,p)),1,.10,.16);
    pencil([V(minX*.97,railY),V(maxX*.97,railY)].map(p=>headPoint(s,p)),0,.10,.16);
  }

  for(let i=0;i<30;i++){
    const angle=mix(Math.PI*.8,Math.PI*1.85,i/29);
    pencil([V(Math.cos(angle)*1.041,Math.sin(angle)*1.041),V(Math.cos(angle+.018)*1.187,Math.sin(angle+.018)*1.187)],2,.055+i*.0015,.105+i*.0015);
  }
  pencil([V(0,-1.43),V(0,1.98)],2,0,.06);
  pencil([V(-1.4,0),V(1.4,0)],2,0,.06);
  pencil([V(.35,1.75),V(.80,1.92),V(1.28,1.92)],1,.13,.17);
  pencil([V(1.02,-.75),V(1.38,-.98),V(1.65,-.98)],1,.14,.18);
  return {settings,prongs};
}